import { useState, type KeyboardEvent } from 'react';
import { cn } from '../lib/cn';

/**
 * Editable list of short strings (domains etc.). Enter, comma, space or blur
 * commits the typed value; Backspace on an empty field removes the last chip.
 */
export function ChipsInput({
  value,
  onChange,
  placeholder,
  normalize = (s) => s.trim(),
  disabled,
  id,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  normalize?: (s: string) => string;
  disabled?: boolean;
  id?: string;
}) {
  const [draft, setDraft] = useState('');

  const commit = () => {
    const items = draft.split(/[\s,;]+/).map(normalize).filter(Boolean);
    const next = [...value];
    for (const item of items) if (!next.includes(item)) next.push(item);
    if (next.length !== value.length) onChange(next);
    setDraft('');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Backspace' && !draft && value.length) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div
      className={cn(
        'flex min-h-8 flex-wrap items-center gap-1 rounded-md border border-line bg-surface px-1.5 py-1 focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/25',
        disabled && 'bg-subtle',
      )}
    >
      {value.map((chip) => (
        <span key={chip} className="inline-flex items-center gap-1 rounded bg-subtle px-1.5 py-0.5 text-xs text-ink">
          {chip}
          {!disabled && (
            <button
              type="button"
              aria-label={`Remove ${chip}`}
              className="text-muted hover:text-danger"
              onClick={() => onChange(value.filter((v) => v !== chip))}
            >
              ×
            </button>
          )}
        </span>
      ))}
      {!disabled && (
        <input
          id={id}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={commit}
          placeholder={value.length ? '' : placeholder}
          className="min-w-24 flex-1 bg-transparent px-1 text-sm text-ink outline-none placeholder:text-muted"
        />
      )}
    </div>
  );
}
